// Create / edit a master product variant, scoped to a family. Category is
// optional — leave blank to inherit the family's catalogCategoryId, or pick
// one explicitly to override. Name is not editable here: it's the family's
// universalName, shown by the parent Variants modal's header instead.
//
// The form is grouped by question, not field type: Identity (brand · origin ·
// state · base unit — the fields that make a variant a variant, and the only
// required ones) always visible; Packaging as one composed row with a live
// summary line; everything else (category, spec, attributes, image, active)
// behind a "More details" disclosure — collapsed on create, expanded on edit.
// The quote flow seeds a create from the requested variant so the operator
// changes the one field that differs instead of filling ten; the BE's
// attribute-aware uniqueness refuses an unchanged save.

import {
  Box,
  Button,
  ChakraSelect,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
  useToast,
} from '@elkaso-app/web-design';
import { useUploadFileApi } from 'apis/config/use-upload-file-api';
import { BrandSelect } from 'components/brand-select';
import {
  EMPTY_UOM_OPTIONS,
  flattenAttributes,
  groupAttributesByType,
  MasterProduct,
  useCreateMasterProductApi,
  useGetUomOptionsApi,
  useUpdateMasterProductApi,
} from 'apis/master-products';
import {
  AttributeDictionaryType,
  dictionaryLabelFor,
  dictionaryValues,
  eavTypes,
  useGetAttributeDictionaryApi,
} from 'apis/product-attributes';
import { EMPTY_BRAND_OPTIONS, useGetBrandOptionsApi } from 'apis/product-brands';
import type { CategoryRef } from 'pages/exclusive-deals/components/editable-deals-grid/editable-deal-row.types';
import { GridCategoryModal } from 'pages/exclusive-deals/components/editable-deals-grid/grid-category-modal';
import {
  buildCanonicalOptions,
  buildMultiCanonicalOptions,
  toCanonicalValue,
} from 'pages/exclusive-deals/utils/canonical-options';
import { packagingSummary } from 'pages/inquiry-processor/components/family-workspace/packaging';
import { SpecTagsInput } from 'pages/inquiry-processor/components/family-workspace/spec-tags-input';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';

import { LabelWithHint, UOM_HELP } from './uom-field-meta';

// Prefill for a create opened from the quote flow: the requested variant's
// identity + packaging, plus its name for the context strip. Ignored in edit.
export interface VariantSeed {
  name?: string | null;
  brand?: string | null;
  origin?: string | null;
  state?: string | null;
  baseUnit?: string | null;
  itemUnit?: string | null;
  netContent?: number | null;
  packUnit?: string | null;
  packSize?: number | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  familyId: number;
  familyImageUrl?: string | null; // family default image, shown when the variant has none
  familyCategoryId?: number; // family's catalogCategoryId, used to scope the attribute dictionary when no override is picked
  product?: MasterProduct | null; // present → edit, absent → create
  seed?: VariantSeed; // create only: prefill from the requested variant
}

interface FormState {
  catalogCategoryId: string; // '' = inherit from family
  categoryTitle: string;
  brand: string;
  origin: string;
  baseUnit: string;
  state: string;
  spec: string;
  imageUrl: string;
  itemUnit: string;
  netContent: string;
  packUnit: string;
  packSize: string;
  isActive: boolean;
  attributes: Record<string, string[]>; // attribute type key -> values (multiple allowed per type)
}

const emptyForm = (): FormState => ({
  catalogCategoryId: '',
  categoryTitle: '',
  brand: '',
  origin: '',
  baseUnit: '',
  state: '',
  spec: '',
  imageUrl: '',
  itemUnit: '',
  netContent: '',
  packUnit: '',
  packSize: '',
  isActive: true,
  attributes: {},
});

const seededForm = (seed?: VariantSeed): FormState => ({
  ...emptyForm(),
  brand: seed?.brand ?? '',
  origin: seed?.origin ?? '',
  state: seed?.state ?? '',
  baseUnit: seed?.baseUnit ?? '',
  itemUnit: seed?.itemUnit ?? '',
  netContent: seed?.netContent != null ? String(seed.netContent) : '',
  packUnit: seed?.packUnit ?? '',
  packSize: seed?.packSize != null ? String(seed.packSize) : '',
});

const SectionLabel = ({ children, mt }: { children: ReactNode; mt?: string }) => (
  <Text fontSize='10px' fontWeight='700' color='gray.500' textTransform='uppercase' letterSpacing='wide' mb='1.5' mt={mt}>
    {children}
  </Text>
);

const fromProduct = (p: MasterProduct): FormState => ({
  catalogCategoryId: p.catalogCategoryId != null ? String(p.catalogCategoryId) : '',
  categoryTitle: '', // resolved by the cascade modal from the id when opened
  brand: p.brand ?? '',
  // Origin/state/UOM normalization needs data that loads asynchronously (the attribute
  // dictionary, and the UOM options endpoint) — neither is available to this module-level
  // factory. Keep raw values here; canonicalization happens in the render path below via
  // toCanonicalValue, which also surfaces anything unrecognised as a "(legacy)" option.
  origin: p.origin ?? '',
  baseUnit: p.baseUnit ?? '',
  state: p.state ?? '',
  spec: p.spec ?? '',
  imageUrl: p.imageUrl ?? '',
  itemUnit: p.itemUnit ?? '',
  netContent: p.netContent != null ? String(p.netContent) : '',
  packUnit: p.packUnit ?? '',
  packSize: p.packSize != null ? String(p.packSize) : '',
  isActive: p.isActive ?? true,
  // Collects ALL values per type (deduped, first-seen order) — a type can
  // legitimately hold multiple values (e.g. Cut = skinless AND head-off).
  attributes: groupAttributesByType(p.attributes),
});

export const VariantFormModal = ({
  isOpen,
  onClose,
  familyId,
  familyImageUrl,
  familyCategoryId,
  product,
  seed,
}: Props) => {
  const toast = useToast();
  const isEdit = !!product;
  const [form, setForm] = useState<FormState>(emptyForm());
  // Collapsed on create (the secondary fields are exactly the overwhelm this
  // layout removes), expanded on edit (that flow exists to touch them).
  const [showMore, setShowMore] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Callers build `seed` inline, so its identity changes every render — a
  // dep on it would reset the form under the user's typing. Read it through
  // a ref at open time instead.
  const seedRef = useRef(seed);
  seedRef.current = seed;
  useEffect(() => {
    if (isOpen) {
      setForm(product ? fromProduct(product) : seededForm(seedRef.current));
      setShowMore(!!product);
    }
  }, [isOpen, product]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  // Category override on the form, falling back to the family's category — the dictionary
  // (and therefore state/origin/attribute options) is scoped to whichever category applies.
  const effectiveCategoryId = form.catalogCategoryId ? Number(form.catalogCategoryId) : familyCategoryId;
  const { data: dictionaryData } = useGetAttributeDictionaryApi({ catalogCategoryId: effectiveCategoryId });
  const dictionary = dictionaryData?.data;
  const stateValues = dictionaryValues(dictionary, 'state');
  const originValues = dictionaryValues(dictionary, 'origin');
  const { data: uomData, isError: isUomError } = useGetUomOptionsApi();
  const uomOptions = uomData ?? EMPTY_UOM_OPTIONS;
  const { data: brandData } = useGetBrandOptionsApi();
  const brandNames = brandData ?? EMPTY_BRAND_OPTIONS;

  // Canonicalized here (not in fromProduct, which cannot call hooks, since it's a
  // module-level factory) so both submit and the render path read the same values — submit
  // must send what the select displays, not the raw stored value (see the regression note in
  // fromProduct above: sending the raw value would disagree with what was shown and edited).
  const canonicalBaseUnit = toCanonicalValue(uomOptions, form.baseUnit);
  const canonicalItemUnit = toCanonicalValue(uomOptions, form.itemUnit);
  const canonicalPackUnit = toCanonicalValue(uomOptions, form.packUnit);
  const canonicalBrand = toCanonicalValue(brandNames, form.brand);

  // Live "1 carton × 12 pieces × 0.8 kg" restatement of the packaging row —
  // four abstract fields read back as one sentence.
  const packagingPreview = packagingSummary({
    baseUnit: canonicalBaseUnit,
    itemUnit: canonicalItemUnit,
    netContent: form.netContent,
    packUnit: canonicalPackUnit,
    packSize: form.packSize,
  });

  const upload = useUploadFileApi({
    onSuccess: (data: any) => {
      const url = data?.url || data?.fileUrl || data?.path || '';
      if (url) set('imageUrl', url);
    },
  });

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    upload.mutate({ body });
  };

  const onDone = () => {
    toast({ title: `Variant ${isEdit ? 'updated' : 'created'}`, status: 'success' });
    onClose();
  };
  const onError = (e: any) => {
    toast({
      title: e?.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} variant`,
      status: 'error',
    });
  };

  const createApi = useCreateMasterProductApi({ onSuccess: onDone, onError });
  const updateApi = useUpdateMasterProductApi({ onSuccess: onDone, onError });
  const isSaving = createApi.isLoading || updateApi.isLoading;

  const onSelectCategory = (cat: CategoryRef) =>
    setForm((f) => ({ ...f, catalogCategoryId: String(cat.id), categoryTitle: cat.title }));

  const submit = () => {
    const missing = [
      !form.state.trim() && 'State',
      !form.brand.trim() && 'Brand',
      !form.baseUnit.trim() && 'Base Unit',
    ].filter(Boolean);
    if (missing.length) {
      toast({ title: `Required: ${missing.join(', ')}`, status: 'warning' });
      return;
    }
    const netContentNum = form.netContent.trim() ? Number(form.netContent) : undefined;
    if (netContentNum !== undefined && (!Number.isFinite(netContentNum) || netContentNum <= 0)) {
      toast({ title: `${UOM_HELP.netContent.label} must be a positive number`, status: 'warning' });
      return;
    }
    const packSizeNum = form.packSize.trim() ? Number(form.packSize) : undefined;
    if (packSizeNum !== undefined && (!Number.isInteger(packSizeNum) || packSizeNum <= 0)) {
      toast({ title: 'Package quantity must be a positive whole number', status: 'warning' });
      return;
    }
    // Create omits empty optionals; edit sends null so the BE clears stored values.
    const emptyTo = isEdit ? null : undefined;
    const attributesPayload = flattenAttributes(form.attributes);
    const payload = {
      familyId,
      catalogCategoryId: form.catalogCategoryId ? Number(form.catalogCategoryId) : undefined,
      brand: canonicalBrand.trim() || undefined,
      origin: form.origin.trim() || emptyTo,
      baseUnit: canonicalBaseUnit.trim(),
      state: form.state.trim(),
      spec: form.spec.trim() || emptyTo,
      imageUrl: form.imageUrl.trim() || emptyTo,
      itemUnit: canonicalItemUnit.trim() || emptyTo,
      netContent: netContentNum ?? emptyTo,
      packUnit: canonicalPackUnit.trim() || emptyTo,
      packSize: packSizeNum ?? emptyTo,
      isActive: form.isActive,
      // Edit always sends the full array (BE replace semantics); create only sends it when
      // non-empty so an untouched Attributes section doesn't send a pointless empty array.
      ...(isEdit || attributesPayload.length > 0 ? { attributes: attributesPayload } : {}),
    };
    if (isEdit && product) updateApi.mutate({ id: product.id, data: payload });
    else createApi.mutate(payload);
  };

  const categoryLabel = form.categoryTitle || (form.catalogCategoryId ? `Category #${form.catalogCategoryId}` : '');

  // Origin/state canonicalization happens here (not in fromProduct) because it depends on the
  // dictionary, which loads asynchronously — mirrors variant-row.tsx's pattern.
  const canonicalOrigin = toCanonicalValue(originValues, form.origin);
  const originOptions = buildCanonicalOptions(originValues, canonicalOrigin);
  const selectedOrigin = originOptions.find((o) => o.value === canonicalOrigin) ?? null;
  // canonicalBrand is declared near brandNames above so submit can reuse it too.
  const brandOptions = buildCanonicalOptions(brandNames, canonicalBrand);
  const selectedBrand = brandOptions.find((o) => o.value === canonicalBrand) ?? null;
  // canonicalBaseUnit is declared near uomOptions above so submit can reuse it too.
  const unitOptions = buildCanonicalOptions(uomOptions, canonicalBaseUnit);
  const selectedUnit = unitOptions.find((o) => o.value === canonicalBaseUnit) ?? null;
  const canonicalState = toCanonicalValue(stateValues, form.state);
  const stateOptions = buildCanonicalOptions(stateValues, canonicalState, dictionaryLabelFor(dictionary, 'state'));
  const selectedState = stateOptions.find((o) => o.value === canonicalState) ?? null;

  // Attributes to render: dictionary EAV types (category-scoped) plus any keys already picked
  // that fell outside that scope (e.g. after a category change) — rendered from the unfiltered
  // pick record so switching category never silently drops/hides an existing selection.
  const dictEavTypes = eavTypes(dictionary);
  const dictEavKeys = new Set(dictEavTypes.map((t) => t.key));
  const extraTypes: AttributeDictionaryType[] = Object.keys(form.attributes)
    .filter((key) => !dictEavKeys.has(key))
    .map((key) => ({ id: 0, key, label: key, isIdentityDefault: false, sortOrder: 0, values: [] }));
  const renderedTypes = [...dictEavTypes, ...extraTypes];

  // canonicalItemUnit/canonicalPackUnit are declared near uomOptions above so submit can reuse them too.
  const itemUnitOptions = buildCanonicalOptions(uomOptions, canonicalItemUnit);
  const selectedItemUnit = itemUnitOptions.find((o) => o.value === canonicalItemUnit) ?? null;
  const packUnitOptions = buildCanonicalOptions(uomOptions, canonicalPackUnit);
  const selectedPackUnit = packUnitOptions.find((o) => o.value === canonicalPackUnit) ?? null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '2xl' }} scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEdit ? 'Edit Variant' : 'New Variant'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!isEdit && seed?.name && (
            <Box
              bg='purple.50'
              border='1px solid'
              borderColor='purple.100'
              rounded='md'
              px='3'
              py='2'
              mb='4'
              fontSize='xs'
              color='gray.700'>
              Prefilled from the requested <b>{seed.name}</b>
              {[seed.brand, seed.origin, seed.state].filter(Boolean).length > 0 && (
                <> ({[seed.brand, seed.origin, seed.state].filter(Boolean).join(' · ')})</>
              )}{' '}
              — change the field(s) the supplier&apos;s variant differs on.
            </Box>
          )}

          <SectionLabel>Identity</SectionLabel>
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap='3'>
            <FormControl isRequired>
              <FormLabel fontSize='sm'>Brand</FormLabel>
              <BrandSelect
                options={brandOptions}
                value={selectedBrand}
                onBrandChange={(brand) => set('brand', brand)}
                placeholder='Select brand'
                isClearable
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize='sm'>Origin</FormLabel>
              <ChakraSelect
                options={originOptions}
                value={selectedOrigin}
                onChange={(opt: any) => set('origin', opt?.value ?? '')}
                placeholder='Search country...'
                isClearable
                menuPortalTarget={document.body}
                menuPosition='fixed'
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize='sm'>State</FormLabel>
              <ChakraSelect
                options={stateOptions}
                value={selectedState}
                onChange={(opt: any) => set('state', opt?.value ?? '')}
                placeholder='Select state'
                isClearable
                menuPortalTarget={document.body}
                menuPosition='fixed'
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize='sm'>
                <LabelWithHint label={UOM_HELP.baseUnit.label} hint={UOM_HELP.baseUnit.hint} />
              </FormLabel>
              <ChakraSelect
                options={unitOptions}
                value={selectedUnit}
                onChange={(opt: any) => set('baseUnit', opt?.value ?? '')}
                placeholder='Select unit'
                isClearable
                menuPortalTarget={document.body}
                menuPosition='fixed'
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </FormControl>
          </Grid>

          <SectionLabel mt='4'>Packaging</SectionLabel>
          {/* One composed row in the preview's reading order — the live
              summary below restates the four fields as one sentence. */}
          <Grid templateColumns={{ base: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }} gap='2'>
            <FormControl>
              <FormLabel fontSize='xs'>
                <LabelWithHint label={UOM_HELP.packUnit.label} hint={UOM_HELP.packUnit.hint} />
              </FormLabel>
              <ChakraSelect
                options={packUnitOptions}
                value={selectedPackUnit}
                onChange={(opt: any) => set('packUnit', opt?.value ?? '')}
                placeholder='Select'
                isClearable
                menuPortalTarget={document.body}
                menuPosition='fixed'
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize='xs'>
                <LabelWithHint label={UOM_HELP.packSize.label} hint={UOM_HELP.packSize.hint} />
              </FormLabel>
              <Input
                type='number'
                min={0}
                step={1}
                value={form.packSize}
                onChange={(e) => set('packSize', e.target.value)}
                placeholder='e.g. 10'
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize='xs'>
                <LabelWithHint label={UOM_HELP.itemUnit.label} hint={UOM_HELP.itemUnit.hint} />
              </FormLabel>
              <ChakraSelect
                options={itemUnitOptions}
                value={selectedItemUnit}
                onChange={(opt: any) => set('itemUnit', opt?.value ?? '')}
                placeholder='Select'
                isClearable
                menuPortalTarget={document.body}
                menuPosition='fixed'
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize='xs'>
                <LabelWithHint label={UOM_HELP.netContent.label} hint={UOM_HELP.netContent.hint} />
              </FormLabel>
              <Input
                type='number'
                min={0}
                step='any'
                value={form.netContent}
                onChange={(e) => set('netContent', e.target.value)}
                placeholder='e.g. 0.7'
              />
            </FormControl>
          </Grid>
          <Text fontSize='xs' color={packagingPreview === '—' ? 'gray.400' : 'gray.600'} mt='1.5'>
            {packagingPreview === '—' ? 'Optional — how the product is packed.' : `= ${packagingPreview}`}
          </Text>

          {isUomError && (
            <Text fontSize='xs' color='red.500' mt='2'>
              Couldn&apos;t load units — reload the page to try again.
            </Text>
          )}

          <Button
            size='sm'
            variant='link'
            color='gray.600'
            fontWeight='600'
            mt='4'
            rightIcon={
              <FiChevronDown
                style={{ transform: showMore ? 'rotate(180deg)' : undefined, transition: 'transform .15s' }}
              />
            }
            onClick={() => setShowMore((v) => !v)}>
            {showMore ? 'Hide details' : 'More details — category, spec, attributes, image'}
          </Button>
          {/* unmountOnExit: the collapsed fields hold no state of their own
              (form state lives above), so unmounting keeps the closed form
              genuinely absent rather than present-but-zero-height. */}
          <Collapse in={showMore} animateOpacity unmountOnExit>
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap='3' mt='3'>
              <FormControl>
                <FormLabel fontSize='sm'>Category</FormLabel>
                <GridCategoryModal
                  value={
                    form.catalogCategoryId ? { id: Number(form.catalogCategoryId), title: form.categoryTitle } : null
                  }
                  onSelect={onSelectCategory}
                  trigger={
                    <Button
                      variant='outline'
                      w='100%'
                      justifyContent='space-between'
                      fontWeight='normal'
                      color={form.catalogCategoryId ? 'gray.800' : 'gray.400'}
                      rightIcon={<FiChevronDown />}>
                      <Box as='span' isTruncated>
                        {categoryLabel || 'Inherits family category'}
                      </Box>
                    </Button>
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize='sm'>Spec</FormLabel>
                <SpecTagsInput
                  value={form.spec}
                  onChange={(spec) => set('spec', spec)}
                  placeholder='e.g. cold pressed'
                />
              </FormControl>
            </Grid>

            {renderedTypes.length > 0 && (
              <>
                <Text fontWeight='semibold' fontSize='sm' mt='4' mb='2'>
                  Attributes
                </Text>
                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap='3'>
                  {renderedTypes.map((type) => {
                    const current = form.attributes[type.key] ?? [];
                    const options = buildMultiCanonicalOptions(
                      type.values.map((v) => v.value),
                      current,
                      dictionaryLabelFor(dictionary, type.key)
                    );
                    const selected = options.filter((o) => current.includes(o.value));
                    return (
                      <FormControl key={type.key}>
                        <FormLabel fontSize='sm'>{type.label}</FormLabel>
                        <ChakraSelect
                          isMulti
                          options={options}
                          value={selected}
                          onChange={(opts: any) =>
                            setForm((f) => {
                              const next = { ...f.attributes };
                              const values = (opts ?? []).map((o: any) => o.value);
                              if (values.length > 0) next[type.key] = values;
                              else delete next[type.key];
                              return { ...f, attributes: next };
                            })
                          }
                          placeholder={`Select ${type.label.toLowerCase()}`}
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition='fixed'
                          styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </FormControl>
                    );
                  })}
                </Grid>
              </>
            )}

            <FormControl mt='3'>
              <FormLabel fontSize='sm'>Image</FormLabel>
              <Flex align='center' gap='3'>
                {form.imageUrl ? (
                  <Image src={form.imageUrl} alt='variant' boxSize='56px' objectFit='cover' rounded='md' />
                ) : familyImageUrl ? (
                  <Box textAlign='center'>
                    <Image
                      src={familyImageUrl}
                      alt='family'
                      boxSize='56px'
                      objectFit='cover'
                      rounded='md'
                      opacity={0.7}
                    />
                    <Text fontSize='2xs' color='gray.400'>
                      Inherited from family
                    </Text>
                  </Box>
                ) : (
                  <Box boxSize='56px' bg='gray.100' rounded='md' />
                )}
                <input ref={fileRef} type='file' accept='image/*' hidden onChange={onFile} />
                <Button
                  size='sm'
                  variant='outline'
                  isLoading={upload.isLoading}
                  onClick={() => fileRef.current?.click()}>
                  {form.imageUrl ? 'Replace image' : 'Upload image'}
                </Button>
                {form.imageUrl && (
                  <Button size='sm' variant='ghost' colorScheme='red' onClick={() => set('imageUrl', '')}>
                    Remove
                  </Button>
                )}
                {upload.isLoading && <Spinner size='sm' color='gray.400' />}
              </Flex>
            </FormControl>

            <Flex align='center' gap='2' mt='4'>
              <Switch
                isChecked={form.isActive}
                onChange={(e) => set('isActive', e.target.checked)}
                colorScheme='green'
              />
              <Text fontSize='sm'>Active</Text>
            </Flex>
          </Collapse>
        </ModalBody>
        <ModalFooter gap='2'>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme='red' onClick={submit} isLoading={isSaving}>
            {isEdit ? 'Save Changes' : 'Create Variant'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
